import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, Receipt, Copy, CheckCircle, MessageCircle } from 'lucide-react'
import { ApiService } from '@/services/api'
import type { OrdoreMiniLicense } from '@/services/api'
import { toast } from 'sonner'

interface GenerateOrdoreMiniLicenseModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (license: OrdoreMiniLicense) => void
}

interface FormData {
  restaurantName: string
  ownerName: string
  ownerPhone: string
  ownerEmail: string
  validityDays: string
  notes: string
}

const initialFormData: FormData = {
  restaurantName: '',
  ownerName: '',
  ownerPhone: '',
  ownerEmail: '',
  validityDays: '365',
  notes: ''
}

export default function GenerateOrdoreMiniLicenseModal({ open, onClose, onSuccess }: GenerateOrdoreMiniLicenseModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [generatedLicense, setGeneratedLicense] = useState<OrdoreMiniLicense | null>(null)
  const [copied, setCopied] = useState(false)

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const resetAndClose = () => {
    setFormData(initialFormData)
    setError(null)
    setGeneratedLicense(null)
    setCopied(false)
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.restaurantName || !formData.ownerName || !formData.ownerPhone) {
      setError('Please fill all required fields')
      return
    }

    setIsLoading(true)
    try {
      const response = await ApiService.createOrdoreMiniLicense({
        restaurantName: formData.restaurantName,
        ownerName: formData.ownerName,
        ownerPhone: formData.ownerPhone,
        ownerEmail: formData.ownerEmail || undefined,
        validityDays: Number(formData.validityDays),
        notes: formData.notes || undefined
      })

      if (response.success) {
        setGeneratedLicense(response.data)
        onSuccess(response.data)
        toast.success('License generated successfully')
      } else {
        setError(response.message || 'Failed to generate license')
        toast.error(response.message || 'Failed to generate license')
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to generate license'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyKey = () => {
    if (!generatedLicense) return
    navigator.clipboard.writeText(generatedLicense.licenseKey)
    setCopied(true)
    toast.success('License key copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  const handleWhatsApp = () => {
    if (!generatedLicense) return
    const digits = generatedLicense.ownerPhone.replace(/\D/g, '')
    const text = encodeURIComponent(
      `Hi, your Ordore Mini license key is: ${generatedLicense.licenseKey}. Valid for ${generatedLicense.validityDays} days. Enter this key in the Ordore Mini app to activate your account.`
    )
    window.open(`https://wa.me/${digits}?text=${text}`, '_blank')
  }

  if (generatedLicense) {
    return (
      <Dialog open={open} onOpenChange={resetAndClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              License Generated
            </DialogTitle>
            <DialogDescription>
              Share this key with {generatedLicense.restaurantName} to activate Ordore Mini.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>License Key</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={generatedLicense.licenseKey}
                  readOnly
                  className="font-mono text-base tracking-wide"
                />
                <Button size="sm" variant="outline" onClick={handleCopyKey}>
                  {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <Alert>
              <AlertDescription>
                Valid for {generatedLicense.validityDays} days, expires {new Date(generatedLicense.expiresAt).toLocaleDateString('en-IN')}.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleWhatsApp} className="gap-2">
              <MessageCircle className="h-4 w-4" />
              Send via WhatsApp
            </Button>
            <Button onClick={resetAndClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Generate New License
          </DialogTitle>
          <DialogDescription>
            Create a new Ordore Mini license for a restaurant
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="restaurantName">Restaurant Name *</Label>
            <Input
              id="restaurantName"
              value={formData.restaurantName}
              onChange={(e) => handleChange('restaurantName', e.target.value)}
              placeholder="e.g., Spice Garden"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ownerName">Owner Name *</Label>
              <Input
                id="ownerName"
                value={formData.ownerName}
                onChange={(e) => handleChange('ownerName', e.target.value)}
                placeholder="e.g., Ramesh Kumar"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ownerPhone">Owner Phone *</Label>
              <Input
                id="ownerPhone"
                value={formData.ownerPhone}
                onChange={(e) => handleChange('ownerPhone', e.target.value)}
                placeholder="+919876543210"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ownerEmail">Owner Email</Label>
            <Input
              id="ownerEmail"
              type="email"
              value={formData.ownerEmail}
              onChange={(e) => handleChange('ownerEmail', e.target.value)}
              placeholder="owner@example.com (optional)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="validityDays">Validity *</Label>
            <Select value={formData.validityDays} onValueChange={(v) => handleChange('validityDays', v)}>
              <SelectTrigger id="validityDays">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days</SelectItem>
                <SelectItem value="365">365 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Optional notes for this license"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={resetAndClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate License
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
